import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  CloseRounded,
  CloudUpload,
  Download,
  ErrorOutline,
} from "@mui/icons-material";
import { useState, ChangeEvent } from "react";
import { v4 as uuid } from "uuid";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { toggleFormBuilder } from "../../Redux/reducers/questionSlice";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";

// ─── types ───────────────────────────────────────────────────────────────────

type Step =
  | "upload"
  | "parsing"
  | "parsed"
  | "validating"
  | "preview_errors"
  | "preview_success"
  | "importing"
  | "done";

type CsvRow = Record<string, string>;

type RowStatus = "pending" | "ok" | "error";

type DisplayRow = {
  rowIndex: number;
  fieldNumber: string;
  type: string;
  varname: string;
  label: string;
  status: RowStatus;
  errors: string[];
};

type PreviewError = {
  csvRow: number;
  fieldNumber: number;
  varname: string;
  field: string;
  message: string;
};

type ValidationResult = {
  totalCsvRows: number;
  questionCount: number;
  errorCount: number;
  errors: PreviewError[];
};

type ImportResponse = {
  importedCount: number;
  importedCsvUrl: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

// ─── simple CSV parser (handles quoted fields and escaped double-quotes) ──────

function parseCSVText(text: string): CsvRow[] {
  const normalised = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines: string[] = [];
  let current = "";
  let insideQuote = false;

  for (let i = 0; i < normalised.length; i++) {
    const ch = normalised[i];
    if (ch === '"') {
      current += ch; // always preserve the quote character in the line
      if (insideQuote && normalised[i + 1] === '"') {
        current += '"'; // preserve second " of escaped pair
        i++;
        // insideQuote unchanged — still inside the quoted field
      } else {
        insideQuote = !insideQuote; // opening or closing delimiter
      }
    } else if (ch === "\n" && !insideQuote) {
      lines.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current.trim()) lines.push(current);

  const parseRow = (line: string): string[] => {
    const fields: string[] = [];
    let i = 0;
    while (i <= line.length) {
      if (line[i] === '"') {
        let value = "";
        i++;
        while (i < line.length) {
          if (line[i] === '"') {
            if (line[i + 1] === '"') {
              value += '"';
              i += 2;
            } else {
              i++;
              break;
            }
          } else {
            value += line[i++];
          }
        }
        fields.push(value);
        if (line[i] === ",") i++;
      } else {
        const start = i;
        while (i < line.length && line[i] !== ",") i++;
        fields.push(line.slice(start, i));
        if (line[i] === ",") i++;
        else break;
      }
    }
    return fields;
  };

  if (lines.length === 0) return [];
  const headers = parseRow(lines[0]);
  return lines.slice(1).filter((l) => l.trim()).map((line) => {
    const values = parseRow(line);
    const row: CsvRow = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] ?? "";
    });
    return row;
  });
}

// ─── component ───────────────────────────────────────────────────────────────

const ImportQuestionsDrawer = ({ open, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const { formId, studyId, formLevel } = useAppSelector(
    (state) => state.question
  );

  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState<string>(uuid());
  const [parsedRows, setParsedRows] = useState<CsvRow[]>([]);
  const [displayRows, setDisplayRows] = useState<DisplayRow[]>([]);
  const [errorCount, setErrorCount] = useState(0);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);

  const prefix =
    formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
  const baseUrl = `${prefix}forms/${formId}/import`;

  const resetState = () => {
    setStep("upload");
    setFile(null);
    setFileInputKey(uuid());
    setParsedRows([]);
    setDisplayRows([]);
    setErrorCount(0);
    setImportResult(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // ── file selection: parse immediately client-side ─────────────────────────

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const selectedFile = e.target.files[0];

    if (selectedFile.size / 1024 / 1024 > 10) {
      toastMessage("warning", "File size must be smaller than 10MB!");
      setFileInputKey(uuid());
      return;
    }

    setFile(selectedFile);
    setStep("parsing");

    try {
      const text = await selectedFile.text();
      const rows = parseCSVText(text);

      if (rows.length === 0) {
        toastMessage("warning", "The CSV file appears to be empty.");
        setStep("upload");
        setFile(null);
        setFileInputKey(uuid());
        return;
      }

      const display: DisplayRow[] = rows.map((row, idx) => ({
        rowIndex: idx + 1,
        fieldNumber: row["Field number"] ?? "",
        type: row["Field Type"] ?? "",
        varname: (row["Variable name"] ?? "").split("#")[0],
        label: row["Field label"] ?? "",
        status: "pending",
        errors: [],
      }));

      setParsedRows(rows);
      setDisplayRows(display);
      setStep("parsed");
    } catch {
      toastMessage("error", "Failed to read the CSV file.");
      setStep("upload");
      setFile(null);
      setFileInputKey(uuid());
    }
  };

  // ── validate rows via JSON API ────────────────────────────────────────────

  const validateRows = async () => {
    setStep("validating");
    try {
      const res = await http.post(`${baseUrl}/validate-rows`, {
        rows: parsedRows,
      });
      const result: ValidationResult = res.data.data;

      // map errors back to display rows
      const errorsByRow = new Map<number, string[]>();
      result.errors.forEach((err) => {
        const key = err.csvRow;
        if (!errorsByRow.has(key)) errorsByRow.set(key, []);
        errorsByRow
          .get(key)!
          .push(`[${err.field}] ${err.message}`);
      });

      setDisplayRows((prev) =>
        prev.map((row) => {
          const rowErrors = errorsByRow.get(row.rowIndex) ?? [];
          return {
            ...row,
            status: rowErrors.length > 0 ? "error" : "ok",
            errors: rowErrors,
          };
        })
      );
      setErrorCount(result.errorCount);
      setStep(result.errorCount > 0 ? "preview_errors" : "preview_success");
    } catch (err) {
      setStep("parsed");
      errorToastMessage(err as Error);
    }
  };

  // ── confirm import via JSON API ───────────────────────────────────────────

  const confirmImport = async () => {
    setStep("importing");
    try {
      const res = await http.post(`${baseUrl}/confirm-rows`, {
        rows: parsedRows,
      });
      setImportResult(res.data.data);
      setStep("done");
    } catch (err) {
      setStep("preview_success");
      errorToastMessage(err as Error);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await http.get(`${baseUrl}/template`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "question-import-template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      errorToastMessage(err as Error);
    }
  };

  const handleDone = () => {
    dispatch(toggleFormBuilder());
    handleClose();
  };

  const handleReupload = () => {
    setFile(null);
    setParsedRows([]);
    setDisplayRows([]);
    setErrorCount(0);
    setFileInputKey(uuid());
    setStep("upload");
  };

  // ── shared rows table ─────────────────────────────────────────────────────

  const renderRowsTable = (showStatus: boolean) => (
    <TableContainer
      sx={{
        flexGrow: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Row</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Field #</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Type</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Variable</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Label</TableCell>
            {showStatus && (
              <TableCell sx={{ whiteSpace: "nowrap" }}>Status</TableCell>
            )}
            {showStatus && (
              <TableCell sx={{ whiteSpace: "nowrap" }}>Error Message</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayRows.map((row) => (
            <TableRow
              key={row.rowIndex}
              sx={
                showStatus && row.status === "error"
                  ? { backgroundColor: "error.50" }
                  : undefined
              }
            >
              <TableCell>{row.rowIndex}</TableCell>
              <TableCell>{row.fieldNumber}</TableCell>
              <TableCell>{row.type}</TableCell>
              <TableCell>{row.varname}</TableCell>
              <TableCell sx={{ maxWidth: 160 }}>
                <Typography variant="body2" noWrap>
                  {row.label}
                </Typography>
              </TableCell>
              {showStatus && (
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {row.status === "pending" && (
                    <Chip label="Pending" size="small" />
                  )}
                  {row.status === "ok" && (
                    <Chip label="OK" color="success" size="small" />
                  )}
                  {row.status === "error" && (
                    <Chip label="Error" color="error" size="small" />
                  )}
                </TableCell>
              )}
              {showStatus && (
                <TableCell>
                  {row.errors.length > 0 ? (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {row.errors.join("\n")}
                    </Typography>
                  ) : row.status === "ok" ? (
                    <Typography variant="body2" color="success.main">
                      —
                    </Typography>
                  ) : null}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ── step renderers ────────────────────────────────────────────────────────

  const renderContent = () => {
    switch (step) {
      // ── upload ────────────────────────────────────────────────────────────
      case "upload":
        return (
          <Stack spacing={3} sx={{ p: 3 }}>
            <Alert severity="info" sx={{ alignItems: "center" }}>
              Import questions from a CSV file. Use the template for correct
              formatting.
            </Alert>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadTemplate}
              sx={{ alignSelf: "flex-start" }}
            >
              Download Sample CSV
            </Button>
            <Divider />
            <Box
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 2,
                p: 5,
                textAlign: "center",
                backgroundColor: "action.hover",
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.selected" },
              }}
              component="label"
            >
              <input
                key={fileInputKey}
                type="file"
                hidden
                accept=".csv,text/csv"
                onChange={handleFileChange}
              />
              <CloudUpload
                sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
              />
              <Typography variant="body1" color="text.primary">
                Click to select or drag and drop CSV file
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Max file size: 10MB
              </Typography>
            </Box>
          </Stack>
        );

      // ── parsing ───────────────────────────────────────────────────────────
      case "parsing":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "60%",
              p: 3,
            }}
          >
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6">Reading file…</Typography>
          </Box>
        );

      // ── parsed (preview before validation) ───────────────────────────────
      case "parsed":
        return (
          <Stack spacing={2} sx={{ p: 3, height: "100%" }}>
            <Alert severity="info">
              <Typography variant="subtitle2">
                {file?.name} — {displayRows.length} row
                {displayRows.length !== 1 ? "s" : ""} detected
              </Typography>
              Review the rows below, then click <strong>Validate</strong> to
              check for errors.
            </Alert>
            {renderRowsTable(false)}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" onClick={handleReupload}>
                Change File
              </Button>
              <Button variant="contained" onClick={validateRows}>
                Validate
              </Button>
            </Box>
          </Stack>
        );

      // ── validating / importing ────────────────────────────────────────────
      case "validating":
      case "importing":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "60%",
              p: 3,
            }}
          >
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6">
              {step === "validating"
                ? "Validating rows…"
                : "Importing questions…"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Please do not close this drawer.
            </Typography>
          </Box>
        );

      // ── preview_errors ────────────────────────────────────────────────────
      case "preview_errors":
        return (
          <Stack spacing={2} sx={{ p: 3, height: "100%" }}>
            <Alert severity="error" icon={<ErrorOutline />}>
              <Typography variant="subtitle2">Validation Failed</Typography>
              Found {errorCount} error{errorCount !== 1 ? "s" : ""} across{" "}
              {displayRows.length} row{displayRows.length !== 1 ? "s" : ""}. Hover
              an <strong>Error</strong> chip to see the details. Fix the CSV
              and re-upload.
            </Alert>
            {renderRowsTable(true)}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleReupload}>
                Re-upload File
              </Button>
            </Box>
          </Stack>
        );

      // ── preview_success ───────────────────────────────────────────────────
      case "preview_success":
        return (
          <Stack spacing={2} sx={{ p: 3, height: "100%" }}>
            <Alert severity="success" icon={<CheckCircleOutline />}>
              <Typography variant="subtitle2">Validation Successful</Typography>
              All {displayRows.length} row
              {displayRows.length !== 1 ? "s" : ""} passed. Ready to import.
            </Alert>
            {renderRowsTable(true)}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" onClick={handleReupload}>
                Back
              </Button>
              <Button variant="contained" onClick={confirmImport}>
                Import Now
              </Button>
            </Box>
          </Stack>
        );

      // ── done ──────────────────────────────────────────────────────────────
      case "done":
        return (
          <Box sx={{ p: 3, textAlign: "center", mt: 5 }}>
            <CheckCircleOutline
              color="success"
              sx={{ fontSize: 80, mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              Import Complete!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Successfully imported {importResult?.importedCount} question
              {importResult?.importedCount !== 1 ? "s" : ""}.
            </Typography>
            {importResult?.importedCsvUrl && (
              <Button
                startIcon={<Download />}
                href={importResult.importedCsvUrl}
                target="_blank"
                sx={{ mb: 4 }}
              >
                Download Imported CSV (Audit)
              </Button>
            )}
            <Divider sx={{ my: 3 }} />
            <Button
              variant="contained"
              onClick={handleDone}
              fullWidth
              size="large"
            >
              Close & Refresh
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: "60vw" } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Import Questions
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseRounded />
        </IconButton>
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>{renderContent()}</Box>
    </Drawer>
  );
};

export default ImportQuestionsDrawer;
